import { useState, useMemo } from "react";
import type { LabReading, LabResult, StatusIndicator, FilterConfig } from "../types";

export interface UseLaboratoryFilteringProps {
  results: LabResult[];
  getReading?: (result: LabResult) => LabReading | undefined;
}

const defaultGetReading = (result: LabResult): LabReading | undefined => result.readings[0];

export const useLaboratoryFiltering = ({
  results,
  getReading = defaultGetReading,
}: UseLaboratoryFilteringProps): FilterConfig => {
  const [activeFilters, setActiveFilters] = useState<StatusIndicator[]>([]);

  const availableFilters = useMemo(() => {
    const indicators = new Set<StatusIndicator>();
    results.forEach((result) => {
      const indicator = getReading(result)?.statusIndicator;
      if (indicator) {
        indicators.add(indicator);
      }
    });
    return Array.from(indicators);
  }, [results, getReading]);

  const filteredResults = useMemo(() => {
    if (activeFilters.length === 0) {
      return results;
    }
    return results.filter((result) => {
      const indicator = getReading(result)?.statusIndicator;
      return indicator && activeFilters.includes(indicator);
    });
  }, [results, activeFilters, getReading]);

  const toggleFilter = (filter: StatusIndicator) => {
    setActiveFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  };

  const clearFilters = () => {
    setActiveFilters([]);
  };

  return {
    activeFilters,
    availableFilters,
    filteredResults,
    toggleFilter,
    clearFilters,
    setActiveFilters,
  };
};

export type UseLaboratoryFilteringReturn = ReturnType<typeof useLaboratoryFiltering>;
